import { Link, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  Info,
  OctagonAlert,
  FileCog,
  ArrowLeft,
} from 'lucide-react'
import {
  useAuth,
  useSwitchJobTelemetry,
  isInternalTrace,
  type SwitchJobTelemetry,
  type TelemetryReason,
} from '@nyos/shared'
import { useYard } from '../../../contexts/YardContext'
import ScreenShell from '../../../components/ScreenShell'

/**
 * Switch Job Story (Plan 26 Phase 1a) — the customer-facing telemetry
 * narrative for one work order: what the engine decided, why, and what it
 * cost — numbers only, no algorithm internals.
 *
 * Data comes from the role-split telemetry endpoint; a non-SUPERADMIN role
 * never receives solver-internal fields (Phase 0c server-side DTO split).
 * A 404 row renders "Telemetry Summary Unavailable".
 */
export default function SwitchJobStoryPage() {
  const { id: workOrderId } = useParams<{ id: string }>()
  const { activeFacilityId } = useYard()
  const { user } = useAuth()
  const telemetry = useSwitchJobTelemetry(activeFacilityId, workOrderId)

  const isSuperAdmin = user?.role === 'ROLE_SUPERADMIN'

  return (
    <ScreenShell
      title="Switch Job Story"
      subtitle="Every decision the engine made — why it chose it, where it ran, what it cost"
      actions={
        <Link
          to="/insights"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All jobs
        </Link>
      }
    >
      <div className="h-full overflow-y-auto p-4 max-w-3xl">
        {telemetry.isPending ? (
          <div className="text-sm text-muted-foreground">Loading story…</div>
        ) : telemetry.isError ? (
          <div className="text-sm text-destructive">
            Couldn't load telemetry — try again.
          </div>
        ) : telemetry.data == null ? (
          <TelemetryUnavailable workOrderId={workOrderId} />
        ) : (
          <StoryBody
            telemetry={telemetry.data}
            isSuperAdmin={isSuperAdmin}
            workOrderId={workOrderId!}
          />
        )}
      </div>
    </ScreenShell>
  )
}

function TelemetryUnavailable({ workOrderId }: { workOrderId?: string }) {
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
      <div className="font-semibold mb-1">⚠ Telemetry Summary Unavailable</div>
      <p className="text-muted-foreground">
        No generation telemetry exists for this job ({workOrderId ?? 'unknown'}).
        It may predate the telemetry ledger, or the generation ran before
        dispatch — previews aren't recorded.
      </p>
    </div>
  )
}

function StoryBody({
  telemetry,
  isSuperAdmin,
  workOrderId,
}: {
  telemetry: SwitchJobTelemetry
  isSuperAdmin: boolean
  workOrderId: string
}) {
  const statusTone =
    telemetry.executionStatus === 'SUCCESS'
      ? 'border-switch-normal/50 bg-switch-normal/10 text-switch-normal'
      : telemetry.executionStatus === 'BLOCKED'
        ? 'border-amber-500/50 bg-amber-500/10 text-amber-500'
        : 'border-destructive/50 bg-destructive/10 text-destructive'

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div className="rounded-lg border border-border bg-card p-4 flex flex-wrap items-center gap-3">
        <span className={`px-2 py-0.5 rounded-md border text-xs font-mono ${statusTone}`}>
          {telemetry.executionStatus}
        </span>
        <span className="text-xs text-muted-foreground">
          Revision v{telemetry.generationVersion}
        </span>
        <span className="text-xs text-muted-foreground">
          Generated {new Date(telemetry.createdAt).toLocaleString()}
        </span>
        {!isInternalTrace(telemetry) && telemetry.dataCompletenessRatio != null && (
          <span className="ml-auto text-xs text-muted-foreground">
            {Math.round(telemetry.dataCompletenessRatio * 100)}% of metrics populated
          </span>
        )}
        {isSuperAdmin && isInternalTrace(telemetry) && (
          <Link
            to={`/admin/switch-trace/${workOrderId}`}
            className="inline-flex items-center gap-1 text-sm text-accent-blue hover:underline"
          >
            <FileCog className="h-3.5 w-3.5" /> Engineering trace
          </Link>
        )}
      </div>

      {/* ROI strip — numbers only */}
      <MetricStrip telemetry={telemetry} />

      {/* Timeline — plain-English reason cards */}
      <section className="rounded-lg border border-border bg-card p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          What happened
        </h3>
        {telemetry.reasonCodes.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            Clean run — every planned move sequenced without violations or
            detours.
          </div>
        ) : (
          <ol className="space-y-2">
            {telemetry.reasonCodes.map((r, i) => (
              <ReasonCard key={`${r.code}-${i}`} reason={r} />
            ))}
          </ol>
        )}
      </section>
    </div>
  )
}

function MetricStrip({ telemetry }: { telemetry: SwitchJobTelemetry }) {
  const measured = Object.entries(telemetry.measuredMetrics ?? {})
  const modeled = Object.entries(telemetry.modeledMetrics ?? {})
  if (measured.length === 0 && modeled.length === 0) {
    return null
  }
  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        What it cost
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {[...measured, ...modeled].map(([key, metric]) => (
          <MetricChip key={key} name={key} metric={metric} />
        ))}
      </div>
    </section>
  )
}

function MetricChip({
  name,
  metric,
}: {
  name: string
  metric: { value: number; tag: string }
}) {
  const tagTone =
    metric.tag === 'MEASURED'
      ? 'border-switch-normal/50 text-switch-normal'
      : metric.tag === 'MODELED'
        ? 'border-accent-blue/50 text-accent-blue'
        : 'border-amber-500/50 text-amber-500'
  return (
    <div className="rounded-md border border-border bg-muted/40 p-2">
      <div className="flex items-center justify-between gap-1">
        <span className="text-xs text-muted-foreground truncate" title={name}>
          {humanMetricName(name)}
        </span>
        <span className={`px-1 rounded border text-xs font-mono shrink-0 ${tagTone}`}>
          {metric.tag}
        </span>
      </div>
      <div className="text-sm font-semibold">{formatMetricValue(metric.value)}</div>
    </div>
  )
}

function ReasonCard({ reason }: { reason: TelemetryReason }) {
  const Icon =
    reason.severity === 'ERROR'
      ? OctagonAlert
      : reason.severity === 'WARNING'
        ? AlertTriangle
        : Info
  const tone =
    reason.severity === 'ERROR'
      ? 'text-destructive border-destructive/40'
      : reason.severity === 'WARNING'
        ? 'text-amber-500 border-amber-500/40'
        : 'text-accent-blue border-accent-blue/40'
  return (
    <li className={`flex items-start gap-2.5 rounded-md border p-3 bg-card ${tone}`}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-sm text-foreground">{reason.humanText}</div>
        <div className="text-xs font-mono text-muted-foreground mt-0.5">
          {reason.code}
        </div>
      </div>
    </li>
  )
}

function humanMetricName(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase())
}

function formatMetricValue(v: number): string {
  if (Number.isInteger(v)) {
    return v.toLocaleString()
  }
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 })
}
